/**
 * Charges hooks using React Relay
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyLoadQuery, usePaginationFragment, fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useChargesRelayQuery as UseChargesRelayQuery,
  useChargesRelayQuery$variables,
  ChargeFilterInput,
  ChargeSortInput,
} from './__generated__/useChargesRelayQuery.graphql';
import type { useChargesRelayDirectQuery as UseChargesRelayDirectQuery } from './__generated__/useChargesRelayDirectQuery.graphql';
import type { useChargesRelayByIdQuery } from './__generated__/useChargesRelayByIdQuery.graphql';
import type { useChargesRelayPaginationFragment$key } from './__generated__/useChargesRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const ChargesListPaginationFragment = graphql`
  fragment useChargesRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useChargesRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "ChargeFilterInput" }
    sort: { type: "[ChargeSortInput!]" }
  ) {
    listCharges(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useChargesRelayPaginationFragment_listCharges"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          amount
          amountRefunded
          chargeTotal
          created
          description
          holdAmount
          paymentIntentId
          refundStatus
          refunded
          status
          systemId
          systemOldId
          taxAmount
          taxRate
          userId
          userOldId
          walletId
          organizationId
          partnerCharge
          user {
            id
            firstName
            lastName
            email
          }
          system {
            id
            oldId
            name
          }
          rental {
            id
          }
        }
      }
    }
  }
`;

const ChargesListQuery = graphql`
  query useChargesRelayQuery(
    $first: Int
    $after: String
    $filter: ChargeFilterInput
    $sort: [ChargeSortInput!]
  ) {
    ...useChargesRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

const ChargesListDirectQuery = graphql`
  query useChargesRelayDirectQuery(
    $first: Int
    $after: String
    $filter: ChargeFilterInput
    $sort: [ChargeSortInput!]
  ) {
    listCharges(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          amount
          amountRefunded
          chargeTotal
          created
          description
          holdAmount
          paymentIntentId
          refundStatus
          refunded
          status
          systemId
          systemOldId
          taxAmount
          taxRate
          userId
          userOldId
          walletId
          organizationId
          partnerCharge
          user {
            id
            firstName
            lastName
            email
          }
          system {
            id
            oldId
            name
          }
          rental {
            id
          }
        }
      }
    }
  }
`;

const ChargeByIdQuery = graphql`
  query useChargesRelayByIdQuery($id: ID!) {
    getCharge(id: $id) {
      id
      amount
      amountRefunded
      chargeTotal
      created
      data
      description
      holdAmount
      paymentIntentId
      refundStatus
      refunded
      status
      systemId
      systemOldId
      taxAmount
      taxRate
      userId
      userOldId
      walletId
      organizationId
      partnerCharge
      user {
        id
        firstName
        lastName
        email
        phone
      }
      system {
        id
        oldId
        name
      }
      rental {
        id
      }
      wallet {
        id
      }
      walletTransaction {
        id
        oldId
      }
      refunds {
        id
        amount
        createdOn
        description
        status
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { ChargeFilterInput, ChargeSortInput };

export interface ChargeUserGraphQL {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: number | null;
}

export interface ChargeSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

export interface ChargeGraphQL {
  id: string;
  amount?: number | null;
  amountRefunded?: number | null;
  chargeTotal?: string | null;
  created?: string | null;
  data?: string | null;
  description?: string | null;
  holdAmount?: number | null;
  paymentIntentId?: string | null;
  refundStatus?: string | null;
  refunded?: boolean | null;
  status?: string | null;
  systemId?: string | null;
  systemOldId?: string | null;
  taxAmount?: string | null;
  taxRate?: string | null;
  userId?: string | null;
  userOldId?: string | null;
  walletId?: string | null;
  organizationId?: string | null;
  partnerCharge?: boolean | null;
  user?: ChargeUserGraphQL | null;
  system?: ChargeSystemGraphQL | null;
  rental?: { id: string } | null;
  wallet?: { id: string } | null;
  walletTransaction?: { id: string; oldId?: string | null } | null;
  refunds?: Array<{
    id: string;
    amount: number;
    createdOn?: string | null;
    description?: string | null;
    status: string;
  }> | null;
}

export interface ChargeQueryFilter {
  systemId?: string;
  systemOldId?: string;
  organizationId?: string;
  userId?: string;
  status?: string;
  createdAfter?: string;
  createdBefore?: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Suspense-based hook for fetching charges list
 */
export function useChargesRelay(
  filter?: ChargeQueryFilter,
  options?: {
    limit?: number;
    sort?: ChargeSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED', order: 'DESC' }];

  const variables: useChargesRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
  };

  const queryData = useLazyLoadQuery<UseChargesRelayQuery>(ChargesListQuery, variables);

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseChargesRelayQuery,
    useChargesRelayPaginationFragment$key
  >(ChargesListPaginationFragment, queryData);

  const nodes = data.listCharges?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as ChargeGraphQL[],
    count: data.listCharges?.count,
    pageInfo: data.listCharges?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Suspense-based hook for fetching a single charge by ID
 */
export function useChargeByIdRelay(chargeId: string) {
  const data = useLazyLoadQuery<useChargesRelayByIdQuery>(ChargeByIdQuery, {
    id: chargeId,
  });

  return data.getCharge as ChargeGraphQL | null;
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for charges data
 */
export function useChargesRelayQuery(
  filter?: ChargeQueryFilter,
  options?: {
    limit?: number;
    sort?: ChargeSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<ChargeGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useChargesRelayQuery$variables = {
          first: limit,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseChargesRelayDirectQuery>(
          relayEnvironment,
          ChargesListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listCharges?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as ChargeGraphQL[]);
          setCount(result.listCharges?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch charges'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching a single charge by ID with explicit loading state
 */
export function useChargeByIdRelayQuery(
  chargeId: string | undefined,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<ChargeGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(chargeId);

  useEffect(() => {
    if (!enabled || !chargeId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<useChargesRelayByIdQuery>(
          relayEnvironment,
          ChargeByIdQuery,
          { id: chargeId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(result.getCharge as ChargeGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch charge'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, chargeId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}

// ============================================================================
// Helpers
// ============================================================================

function buildRelayFilter(filter?: ChargeQueryFilter): ChargeFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: ChargeFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.userId) {
    relayFilter.userId = { eq: filter.userId };
  }
  if (filter.status) {
    relayFilter.status = { eq: filter.status };
  }
  if (filter.createdAfter || filter.createdBefore) {
    relayFilter.created = {};
    if (filter.createdAfter) {
      relayFilter.created.greaterThanOrEqual = filter.createdAfter;
    }
    if (filter.createdBefore) {
      relayFilter.created.lessThanOrEqual = filter.createdBefore;
    }
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}
